import { FlakinessReport } from '@flakiness/flakiness-report';
import { WireReport } from '@flakiness/server/common/wireReport.js';
import { humanReadableMs } from '@flakiness/shared/common/utils.js';
import { consume, ContextType } from '@lit/context';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { artifactFromAttachment, AttachmentUrlBuilder, ClassifiedArtifacts, classifyArtifacts } from '../artifact.js';
import { contexts } from '../contexts.js';
import { createCodeSnippet } from './code-snippet.js';
import { linkStyles } from './cssstyles.js';
import { isHTMLAttachment } from './html-attachment.js';
import { isTextAttachment } from './text-attachment.js';

type BadgeKind = 'image' | 'video' | 'pw-trace' | 'paperclip';

@customElement('test-step')
class TestStep extends LitElement {
  @property({ type: Boolean, reflect: true }) open?: boolean;
  @property({ type: Boolean, attribute: false }) wireReport?: WireReport;
  @property({ type: Boolean, attribute: false }) step?: FlakinessReport.TestStep;
  @property({ type: Boolean, attribute: false }) commitId?: FlakinessReport.CommitId;
  @property({ attribute: false }) attachmentURLBuilder?: AttachmentUrlBuilder;
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;

  protected override willUpdate(changed: PropertyValues): void {
    if (changed.has('step'))
      this.open = !!this.step?.error;
  }

  override render() {
    const ansiSnippet = this.step?.snippet;
    const htmlSnippet = createCodeSnippet(this.wireReport?.report, this.step?.location, 1);
    const hasSnippet = !!ansiSnippet || htmlSnippet;
    const attachments = this.step?.attachments ?? [];
    const builder = this.attachmentURLBuilder;
    const artifacts = builder ? attachments.map(attachment => artifactFromAttachment(attachment, builder)) : [];
    const classified = classifyArtifacts(artifacts);
    const hasArtifacts = !!artifacts.length;
    const clickable = !!this.step?.steps?.length || !!hasSnippet || hasArtifacts;
    return html`
      <v-box>
        <h-box class=${classMap({ header: true, clickable })} header @click=${clickable ? () => { this.open = !this.open; } : nothing }>
          <sl-icon name=${this.open ? 'chevron-down' : 'chevron-right'}></sl-icon>
          <fk-status ?success=${!this.step?.error}></fk-status>
          <div class=step-description>
            ${this.step?.title}
            ${this.step?.location && this.commitId ? html`<span class=step-location>— <a-location .project=${this._project} .location=${this.step.location} .commitId=${this.commitId}></a-location></span>` : nothing}
          </div>
          ${hasArtifacts ? this._renderAttachmentBadges(classified) : nothing}
          <x-filler></x-filler>
          <span class=step-duration>${humanReadableMs(this.step?.duration ?? 0)}</span>
        </h-box>
        ${clickable ? html`
          ${ansiSnippet ? html`<ansi-text class=snippet .text=${ansiSnippet}></ansi-text>` : nothing}
          ${htmlSnippet ? html`<code-snippet class=snippet .location=${this.step?.location} .snippet=${htmlSnippet}></code-snippet>` : nothing}
          ${hasArtifacts ? this._renderAttachments(classified) : nothing}
          <div class=substeps>
            <v-box>
              ${this.step?.steps?.map(step => html`<test-step .commitId=${this.commitId} .wireReport=${this.wireReport} .step=${step} .attachmentURLBuilder=${this.attachmentURLBuilder}></test-step>`)}
            </v-box>
          </div>
        ` : nothing}
      </v-box>
    `;
  }

  private _renderAttachmentBadges({ imageDiffs, screenshots, videos, traces, misc }: ClassifiedArtifacts) {
    const groups: Array<{ kind: BadgeKind, names: string[] }> = [
      { kind: 'image', names: [...imageDiffs.map(d => d.name), ...screenshots.map(s => s.fileName)] },
      { kind: 'video', names: videos.map(v => v.fileName) },
      { kind: 'pw-trace', names: traces.map(t => t.fileName) },
      { kind: 'paperclip', names: misc.map(m => m.fileName) },
    ];
    return html`
      <h-box class=attachment-badges>
        ${groups.map(({ kind, names }) => names.length ? html`
          <span class=badge title=${names.join('\n')}>
            ${kind === 'paperclip' ? html`<sl-icon name=paperclip></sl-icon>` : html`<artifact-icon .type=${kind}></artifact-icon>`}
            ${names.length > 1 ? html`<span class=badge-count>${names.length}</span>` : nothing}
          </span>
        ` : nothing)}
      </h-box>
    `;
  }

  private _renderAttachments({ imageDiffs, screenshots, videos, traces, misc }: ClassifiedArtifacts) {
    return html`
      <v-box class=step-attachments>
        ${imageDiffs.map(diff => html`<image-diff-attachment .diff=${diff}></image-diff-attachment>`)}
        ${screenshots.length ? html`
          <div class=row-wrap>
            ${screenshots.map(artifact => html`<image-attachment .artifact=${artifact}></image-attachment>`)}
          </div>
        ` : nothing}
        ${videos.map(artifact => html`<video-attachment .artifact=${artifact}></video-attachment>`)}
        ${traces.length ? html`
          <div class=row-wrap>
            ${traces.map(artifact => html`<trace-attachment .artifact=${artifact}></trace-attachment>`)}
          </div>
        ` : nothing}
        ${misc.map(artifact => isHTMLAttachment(artifact) ? html`
          <html-attachment .artifact=${artifact}></html-attachment>
        ` : isTextAttachment(artifact) ? html`
          <text-attachment .artifact=${artifact}></text-attachment>
        ` : html`
          <artifact-link .artifact=${artifact}></artifact-link>
        `)}
      </v-box>
    `;
  }

  static styles = [linkStyles, css`
    .substeps, .snippet, .step-attachments {
      display: none;
      margin-left: var(--sl-spacing-2x-large);
    }

    ansi-text.snippet {
      padding: var(--sl-spacing-medium);
      background-color: var(--sl-color-neutral-50);
    }

    .header sl-icon {
      visibility: hidden;
      flex: none;
    }

    .header.clickable {
      cursor: pointer;

      sl-icon {
        visibility: visible;
      }
    }

    .step-description {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      display: flex;
      gap: var(--sl-spacing-x-small);
      overflow: hidden;
    }

    .step-duration {
      white-space: nowrap;
      flex: none;
    }

    .step-location {
      color: var(--sl-color-neutral-500);
      display: flex;
      gap: var(--sl-spacing-x-small);
      overflow: hidden;
      align-items: start;
    }

    .attachment-badges {
      flex: none;
      gap: var(--sl-spacing-x-small);
      align-items: center;
      color: var(--sl-color-neutral-500);
    }

    .badge {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      font-size: var(--sl-font-size-x-small);
      line-height: 1;
    }

    .badge-count {
      font-family: var(--sl-font-mono);
      font-variant-numeric: tabular-nums;
    }

    .badge sl-icon {
      font-size: 0.95em;
    }

    .step-attachments {
      gap: var(--sl-spacing-medium);
      padding: var(--sl-spacing-small) 0;
      --image-attachment-thumb-width: 120px;
      --video-attachment-max-width: 320px;
      --trace-attachment-thumb-width: 120px;
    }

    .step-attachments image-diff-attachment {
      --image-diff-max-width: 400px;
    }

    .row-wrap {
      display: flex;
      flex-wrap: wrap;
      gap: var(--sl-spacing-medium);
    }

    :host {
      display: block;
    }

    :host([open]) {
      .substeps, .snippet {
        display: block;
      }
      .step-attachments {
        display: flex;
      }
    }
  `];
}
